import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/apiService';

interface AdminUser {
  id: string;
  name: string;
  email: string;
  userType: string;
  createdAt: string;
  isActive: boolean;
  mentorProfile: { id: string } | null;
}

const PAGE_SIZE = 25;

const AdminUsers: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [actingOn, setActingOn] = useState<string | null>(null);

  const fetchUsers = useCallback(
    async (query: string, offset: number, append: boolean) => {
      setLoading(true);
      const result = await apiService.getAdminUsers(query, PAGE_SIZE, offset);
      if (!result.success) {
        setDenied(true);
        setLoading(false);
        return;
      }
      setUsers((prev) => (append ? [...prev, ...(result.users || [])] : result.users || []));
      setTotal(result.total || 0);
      setLoading(false);
    },
    []
  );

  useEffect(() => {
    fetchUsers(search, 0, false);
  }, [search, fetchUsers]);

  const handleSearch = () => setSearch(searchInput.trim());

  const handleToggleActive = async (user: AdminUser) => {
    if (actingOn) return;
    if (
      user.isActive &&
      !window.confirm(`Deactivate ${user.name} (${user.email})? They will be signed out and unable to log in.`)
    ) {
      return;
    }
    setActingOn(user.id);
    const result = await apiService.setUserActive(user.id, !user.isActive);
    setActingOn(null);
    if (result.success) {
      setUsers((prev) =>
        prev.map((u) => (u.id === user.id ? { ...u, isActive: !user.isActive } : u))
      );
    } else {
      window.alert(result.error || result.message || 'Could not update user.');
    }
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  if (denied) {
    return (
      <div className="section">
        <div className="wrap" style={{ maxWidth: 760 }}>
          <div className="card" style={{ maxWidth: 520 }}>
            <h3 style={{ marginBottom: 8 }}>Admin access required</h3>
            <p className="meta">Sign in with an admin account to manage users.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="section">
      <div className="wrap" style={{ maxWidth: 960 }}>
        <span className="eyebrow" style={{ display: 'block', marginBottom: 8 }}>
          Internal · admin
        </span>
        <h1 style={{ fontSize: 30, marginBottom: 8 }}>User management</h1>
        <p className="meta" style={{ marginBottom: 24 }}>
          {total} user{total !== 1 ? 's' : ''} · deactivating blocks login and API access.
        </p>

        <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
          <input
            type="search"
            placeholder="Search by name or email…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            style={{ maxWidth: 320 }}
          />
          <button className="btn btn-secondary btn-sm" onClick={handleSearch}>
            Search
          </button>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
            <Link className="btn btn-secondary btn-sm" to="/admin/ops">
              Verification queue
            </Link>
            <Link className="btn btn-secondary btn-sm" to="/admin/feedback">
              User feedback
            </Link>
            <Link className="btn btn-secondary btn-sm" to="/admin/templates">
              Offering templates
            </Link>
          </div>
        </div>

        <div className="card">
          {loading && users.length === 0 ? (
            <p className="meta">Loading users…</p>
          ) : users.length === 0 ? (
            <p className="meta">No users found{search ? ` for “${search}”` : ''}.</p>
          ) : (
            <table className="data">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Roles</th>
                  <th>Joined</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{u.name}</div>
                      <div className="meta">{u.email}</div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {(u.userType || 'student')
                          .split(',')
                          .map((r) => r.trim())
                          .filter(Boolean)
                          .map((role) => (
                            <span key={role} className="tag">
                              {role}
                            </span>
                          ))}
                        {u.mentorProfile && <span className="tag solid">mentor profile</span>}
                      </div>
                    </td>
                    <td className="meta">{formatDate(u.createdAt)}</td>
                    <td>
                      {u.isActive ? (
                        <span className="tag">Active</span>
                      ) : (
                        <span
                          className="tag"
                          style={{ color: 'var(--cardinal)', borderColor: 'var(--cardinal)' }}
                        >
                          Deactivated
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        className="btn btn-secondary btn-sm"
                        disabled={actingOn === u.id}
                        onClick={() => handleToggleActive(u)}
                      >
                        {u.isActive ? 'Deactivate' : 'Reactivate'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {!loading && users.length < total && (
            <div style={{ marginTop: 16 }}>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => fetchUsers(search, users.length, true)}
              >
                Load more ({total - users.length} remaining)
              </button>
            </div>
          )}
          {loading && users.length > 0 && (
            <p className="meta" style={{ marginTop: 16 }}>
              Loading…
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminUsers;
